import { useCallback, useEffect, useMemo, useState } from 'react';
import { Minus, Plus, RotateCcw, Search } from 'lucide-react';
import { Modal } from '../../Modal';
import { Button, IconButton, Input, ScrollArea, Typography } from '../ui';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import {
  applyDevMockCounts,
  clearDevMockData,
  devMockAvailable,
  devMockCounts,
  lastKnownServerCounts,
  matchesMockSearch,
  MOCK_ENTITIES,
  probeServerCounts,
  type MockEntityKey,
  type MockScope,
  type ServerCounts,
} from '../../../lib/dev-mock-data';
import styles from './DevMockDataPanel.module.css';

// The "Mock data" panel behind the brand-mark dev menu: every entity, how many of it actually exist,
// and a number you can set to anything (including 0) to check the design at that size. Everything is
// local to this browser — see lib/dev-mock-data.ts for the model. Dev-only, so the copy is not
// localized (same call as the menu's "Reset animations").

const GROUPS: { scope: MockScope; title: string; note: string }[] = [
  { scope: 'account', title: 'Account', note: 'One list for the whole account.' },
  {
    scope: 'workspace',
    title: 'Per workspace',
    note: 'One number applied to every workspace — whichever you open shows that many.',
  },
];

/**
 * Third gate, independent of the two in front of it: the panel refuses to render without a dev build
 * or a server grant, no matter who mounts it. Kept as a wrapper so the body's hooks stay
 * unconditional.
 */
export function DevMockDataPanel(props: { open: boolean; onClose: () => void }) {
  if (!devMockAvailable()) return null;
  return <MockDataPanel {...props} />;
}

function MockDataPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  // The edit in progress. A key present = fake it with this many; absent = leave it to the server.
  const [draft, setDraft] = useState<Partial<Record<MockEntityKey, number>>>({});
  const [server, setServer] = useState<ServerCounts>({});
  const [counting, setCounting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setDraft(devMockCounts());
    setServer(lastKnownServerCounts());
    setSearch('');
    setCounting(true);
    let cancelled = false;
    probeServerCounts((url) => apiFetch(url, { credentials: 'include' }), API_BASE)
      .then((counts) => {
        if (!cancelled) setServer((prev) => ({ ...prev, ...counts }));
      })
      .finally(() => {
        if (!cancelled) setCounting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const set = useCallback((key: MockEntityKey, value: number | undefined) => {
    setDraft((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[key];
      else {
        const ceiling = MOCK_ENTITIES.find((e) => e.key === key)?.max ?? Infinity;
        next[key] = Math.min(ceiling, Math.max(0, Math.floor(value)));
      }
      return next;
    });
  }, []);

  // Both actions reload: the data layer's caches (React Query, the module stores) are in memory, and
  // a reload is the one refresh that is guaranteed to be complete.
  const apply = () => {
    applyDevMockCounts(draft);
    window.location.reload();
  };
  const useServerData = () => {
    clearDevMockData();
    window.location.reload();
  };

  const visible = useMemo(() => MOCK_ENTITIES.filter((entity) => matchesMockSearch(entity, search)), [search]);
  const fakedCount = Object.keys(draft).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={620}
      title="Mock data"
      footer={
        <>
          <Button variant="outlined" onClick={useServerData}>
            Use server data
          </Button>
          <Button onClick={apply}>Apply &amp; reload</Button>
        </>
      }
    >
      <Typography variant="p3" color="secondary">
        Fake how many of each entity THIS browser sees, to check a design at 0, 1 or 40. Local to you and to dev builds —
        reads only, so writes still go to the server and your account is untouched.{' '}
        <strong>“Use server data” drops every local change.</strong>
      </Typography>

      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onClear={() => setSearch('')}
        startIcon={<Search size={16} aria-hidden />}
        placeholder="Search properties…"
        aria-label="Search mock data properties"
      />

      <ScrollArea className={styles.list}>
        {GROUPS.map(({ scope, title, note }) => {
          const rows = visible.filter((entity) => entity.scope === scope);
          if (rows.length === 0) return null;
          return (
          <section key={scope}>
            <div className={styles.groupHead}>
              <Typography variant="p3" color="strong" component="span">
                {title}
              </Typography>
              <Typography variant="p3" color="muted" component="span">
                {note}
              </Typography>
            </div>

            {rows.map((entity) => {
              const faked = draft[entity.key] !== undefined;
              const real = server[entity.key];
              // What the stepper acts on: the faked number, else the real count, else the fallback.
              const shown = draft[entity.key] ?? real ?? entity.fallback;
              return (
                <div key={entity.key} className={styles.row} data-nested={entity.within ? '' : undefined} data-faked={faked ? '' : undefined}>
                  <div className={styles.labels}>
                    <Typography variant="p2" color={faked ? 'strong' : 'default'} component="span">
                      {entity.within ? `↳ ${entity.label}` : entity.label}
                    </Typography>
                    {entity.hint && (
                      <Typography variant="p3" color="muted" component="span">
                        {entity.hint}
                      </Typography>
                    )}
                  </div>

                  <span className={styles.real}>
                    {real !== undefined ? `${real} on the server` : counting ? 'counting…' : 'unknown'}
                  </span>

                  <div className={styles.stepper}>
                    <IconButton
                      aria-label={`One fewer ${entity.label}`}
                      size="tiny"
                      variant="outlined"
                      disabled={shown <= 0}
                      onClick={() => set(entity.key, shown - 1)}
                    >
                      <Minus size={12} aria-hidden />
                    </IconButton>
                    {/* A bare number field, not the DS Input: it sits inside a 26px stepper, which is
                        below the Input's 42px box. */}
                    <input
                      className={styles.count}
                      type="number"
                      min={0}
                      max={entity.max}
                      inputMode="numeric"
                      aria-label={`How many ${entity.label}`}
                      value={faked ? String(draft[entity.key]) : ''}
                      placeholder={String(shown)}
                      onChange={(e) => set(entity.key, e.target.value === '' ? undefined : Number(e.target.value))}
                    />
                    <IconButton
                      aria-label={`One more ${entity.label}`}
                      size="tiny"
                      variant="outlined"
                      disabled={shown >= (entity.max ?? Infinity)}
                      onClick={() => set(entity.key, shown + 1)}
                    >
                      <Plus size={12} aria-hidden />
                    </IconButton>
                    <IconButton
                      aria-label={`Take ${entity.label} back from the server`}
                      size="tiny"
                      variant="text"
                      disabled={!faked}
                      onClick={() => set(entity.key, undefined)}
                    >
                      <RotateCcw size={12} aria-hidden />
                    </IconButton>
                  </div>
                </div>
              );
            })}
          </section>
          );
        })}
        {visible.length === 0 && (
          <Typography variant="p3" color="muted" component="p">
            Nothing matches “{search}”.
          </Typography>
        )}
      </ScrollArea>

      <Typography variant="p3" color="muted">
        {fakedCount === 0 ? 'Nothing faked — every screen is reading the server.' : `${fakedCount} faked. Empty a field to hand it back.`}
        {' '}Two rows aren’t counts: <strong>Plan tier</strong> walks Free → Paid → Max+, and a dashboard can only be
        present or absent (it is HTML, not a list). Both are reads — no checkout, no write, and your real plan is untouched.
      </Typography>
    </Modal>
  );
}
